import type { FilterModeType, FilterRelative, FilterType } from "../rift/types";

/** Minimum shape a board filter row must have for rift conversion. */
export interface BoardFilterRow {
  columns: { datasetId: string; accessor: string }[];
  mode: FilterModeType;
  value: unknown;
  relative: FilterRelative | null;
  type: string | null;
}

const VALUELESS_MODES = new Set<FilterModeType>(["EMPTY", "NOT_EMPTY"]);

/**
 * Sentinel `value` marking a boolean board filter as inactive ("Any value").
 *
 * Boolean filters are mode-driven (`IS_TRUE` / `IS_FALSE`), so the `value`
 * column is repurposed to encode active vs. inactive. Legacy boolean rows only
 * ever store `null` / `true` / `false`, so this sentinel is backward-compatible:
 * only rows explicitly set to it are treated as incomplete (not applied).
 */
export const BOOLEAN_FILTER_INACTIVE = "__INACTIVE__";

/** Whether a board filter row has all required values to be applied. */
export const isBoardFilterComplete = (f: BoardFilterRow): boolean => {
  if (f.columns.length === 0) return false;
  if (f.mode === "IS_TRUE" || f.mode === "IS_FALSE") {
    return f.value !== BOOLEAN_FILTER_INACTIVE;
  }
  if (VALUELESS_MODES.has(f.mode)) return true;
  if (f.mode === "RELATIVE") return f.relative != null;
  if (f.mode === "BETWEEN") {
    const range = f.value as { start?: string; end?: string } | null;
    return !!range?.start && !!range?.end;
  }
  if (f.mode === "IN" || f.mode === "NOT_IN") {
    return Array.isArray(f.value) && f.value.length > 0;
  }
  return f.value != null && f.value !== "";
};

/**
 * Maps a board filter's persisted type to the appropriate rift mode and value.
 * `SINGLE` maps to EQUAL, `MULTI` maps to IN, everything else passes through.
 */
const toRiftModeAndValue = (
  f: BoardFilterRow,
): { mode: FilterModeType; value: unknown } => {
  if (f.type === "SINGLE") return { mode: "EQUAL", value: f.value };
  if (f.type === "MULTI") return { mode: "IN", value: f.value };
  return { mode: f.mode, value: f.value };
};

/**
 * Converts an array of board filter DB rows into `FilterType[]` scoped to a
 * specific dataset. Only columns matching the given `datasetId` are included;
 * filters without matching columns or incomplete values are skipped.
 *
 * This is a pure function usable on both the API and the web client.
 */
export const boardFiltersToRift = (
  filters: BoardFilterRow[],
  datasetId: string,
): FilterType[] => {
  return filters.filter(isBoardFilterComplete).flatMap((f) => {
    const matchingAccessors = f.columns
      .filter((c) => c.datasetId === datasetId)
      .map((c) => c.accessor);
    if (matchingAccessors.length === 0) return [];

    const { mode, value } = toRiftModeAndValue(f);
    return [
      {
        columns: matchingAccessors,
        logicOperator: "AND" as const,
        mode,
        relative: f.relative,
        value,
      },
    ];
  });
};
